import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import { useAuth } from "@/context/AuthContext";
import { isStrictAdmin } from "@/lib/rbac";
import AdminLayout from "@/components/AdminLayout";
import EmployeeLookup from "@/components/EmployeeLookup";
import LetterRichEditor from "@/components/LetterRichEditor";
import LetterBodyView from "@/components/LetterBodyView";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import {
  Plus,
  FileSignature,
  Download,
  Trash2,
  Eye,
  Loader2,
  Search,
  RefreshCw,
  X,
} from "lucide-react";

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

const PREHIRE_TYPES = ["Offer Letter", "Internship Offer Letter", "Contract Offer Letter"];

function empLabel(e) {
  if (!e) return "";
  const name = [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" ");
  return `${name} (${e.basic?.code || ""})`;
}

function candidateAsLookup(c) {
  return {
    id: c.id,
    basic: {
      code: c.code,
      first_name: c.first_name,
      middle_name: c.middle_name,
      last_name: c.last_name,
    },
    contact: { official_email: c.email },
  };
}

function approvalBadge(l) {
  const s = l?.approval_status || "Approved";
  if (s === "Approved" || s === "Issued") {
    return (
      <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Issued</span>
    );
  }
  if (s === "Rejected") {
    return (
      <span className="text-[11px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">Rejected</span>
    );
  }
  return (
    <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Pending Approval</span>
  );
}

/** Name shown in the Approver column (badge stays in Status). */
function approvedByValue(l) {
  const s = l?.approval_status || "Approved";
  if (s === "Rejected") {
    return l?.rejected_by || l?.approved_by || "—";
  }
  if (s === "Approved" || s === "Issued") {
    return l?.approved_by || "—";
  }
  return l?.pending_with || "—";
}

/** Labeled approval person line for the view modal (separate from Issued By). */
function approvedByViewLine(l) {
  const s = l?.approval_status || "Approved";
  if (s === "Rejected") {
    const by = l?.rejected_by || l?.approved_by;
    return by ? `Approver: ${by}` : null;
  }
  if (s === "Approved" || s === "Issued") {
    return l?.approved_by ? `Approver: ${l.approved_by}` : null;
  }
  return l?.pending_with ? `Approver: ${l.pending_with}` : null;
}

function escapeLetterHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Format a rupee amount for letter body (matches backend {{previous_ctc}} style). */
function formatCtcInr(raw) {
  const n = Number(String(raw || "").replace(/[,₹\s]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return "";
  return `₹ ${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Inject Previous Annual CTC into rendered letter HTML.
 * Empty <b></b> after substitution is hard to type into in contenteditable — this is the reliable path.
 */
function injectPreviousCtcIntoBody(html, value) {
  const formatted = formatCtcInr(value) || String(value || "").trim();
  if (!formatted || !html) return html;
  const safe = escapeLetterHtml(formatted);
  if (/\{\{\s*previous_ctc\s*\}\}/i.test(html)) {
    return html.replace(/\{\{\s*previous_ctc\s*\}\}/gi, safe);
  }
  if (/(Previous\s+Annual\s+CTC(?:&nbsp;|\u00a0|\s)*:\s*)(?:<b>(?:\s|&nbsp;|\u00a0)*<\/b>)/i.test(html)) {
    return html.replace(
      /(Previous\s+Annual\s+CTC(?:&nbsp;|\u00a0|\s)*:\s*)(?:<b>(?:\s|&nbsp;|\u00a0)*<\/b>)/i,
      `$1<b>${safe}</b>`
    );
  }
  // Re-apply over whatever is currently inside the Previous CTC bold span
  return html.replace(
    /(Previous\s+Annual\s+CTC(?:&nbsp;|\u00a0|\s)*:\s*)<b>[\s\S]*?<\/b>/i,
    `$1<b>${safe}</b>`
  );
}

export default function Letters() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || isStrictAdmin(user);
  const canShowDownload = (l) => !!l?.can_download;
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [types, setTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [mode, setMode] = useState("single"); // single | bulk
  const [recipientKind, setRecipientKind] = useState("employee"); // employee | candidate
  const [step, setStep] = useState(1);
  const [subjectId, setSubjectId] = useState("");
  const [subjectIds, setSubjectIds] = useState([]);
  const [type, setType] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [requireAck, setRequireAck] = useState(false);
  const [previewLetterNo, setPreviewLetterNo] = useState("");
  const [needPreviousCtc, setNeedPreviousCtc] = useState(false);
  const [fillPreviousCtc, setFillPreviousCtc] = useState("");
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [pendingCandidateId, setPendingCandidateId] = useState("");

  const load = async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/letters"),
      api.get("/employees"),
      api.get("/letter-templates"),
      api.get("/candidates").catch(() => ({ data: [] })),
    ]);
    setItems(a.data);
    setEmployees(b.data);
    setTypes(c.data);
    setCandidates((d.data || []).filter((x) => ["Draft", "Offered", "Accepted"].includes(x.status)));
  };

  useEffect(() => {
    load();
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();

  /** Keep ?action=generate&for=candidate (etc.) while the issue modal is open for sidebar active state. */
  const clearLetterQueryParams = () => {
    if (!searchParams.get("action") && !searchParams.get("candidate_id") && !searchParams.get("for")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    next.delete("candidate_id");
    next.delete("for");
    setSearchParams(next, { replace: true });
  };

  const closeCreate = () => {
    setCreateOpen(false);
    clearLetterQueryParams();
  };

  useEffect(() => {
    const action = searchParams.get("action");
    const candId = searchParams.get("candidate_id") || "";
    const forCandidate = searchParams.get("for") === "candidate";
    if (!action && !candId && !forCandidate) return;
    if (createOpen) return; // already open — do not reset wizard mid-flow
    if (action === "generate" || candId || forCandidate) {
      startNew({ candidateId: candId || undefined, forceCandidate: forCandidate || Boolean(candId) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!createOpen || !pendingCandidateId || !candidates.length) return;
    if (candidates.some((c) => c.id === pendingCandidateId)) {
      setRecipientKind("candidate");
      setSubjectId(pendingCandidateId);
      setPendingCandidateId("");
    }
  }, [createOpen, pendingCandidateId, candidates]);

  const empMap = useMemo(
    () => Object.fromEntries(employees.map((e) => [e.id, e])),
    [employees]
  );

  const candidateLookup = useMemo(
    () => candidates.map(candidateAsLookup),
    [candidates]
  );

  const candMap = useMemo(
    () => Object.fromEntries(candidateLookup.map((c) => [c.id, c])),
    [candidateLookup]
  );

  const directory = recipientKind === "candidate" ? candidateLookup : employees;
  const dirMap = recipientKind === "candidate" ? candMap : empMap;

  const issueTypes = useMemo(() => {
    if (recipientKind === "candidate") {
      return types.filter((t) => PREHIRE_TYPES.includes(t));
    }
    return types;
  }, [types, recipientKind]);

  const filteredItems = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((l) => {
      if (typeFilter && l.type !== typeFilter) return false;
      if (!s) return true;
      return (
        (l.employee_name || "").toLowerCase().includes(s) ||
        (l.employee_code || "").toLowerCase().includes(s) ||
        (l.type || "").toLowerCase().includes(s) ||
        (l.title || "").toLowerCase().includes(s) ||
        (l.letter_number || "").toLowerCase().includes(s)
      );
    });
  }, [items, search, typeFilter]);

  const { items: sortedItems, sortBy, sortDir, requestSort } = useSortableData(filteredItems, {
    initialKey: "issue_date",
    initialDir: "desc",
  });

  const startNew = ({ candidateId, forceCandidate } = {}) => {
    setMode("single");
    setStep(1);
    setRecipientKind(candidateId || forceCandidate ? "candidate" : "employee");
    setSubjectId(candidateId || "");
    setSubjectIds([]);
    setType(candidateId || forceCandidate ? "Offer Letter" : "");
    setTitle("");
    setBody("");
    setIssueDate("");
    setRequireAck(false);
    setPreviewLetterNo("");
    setNeedPreviousCtc(false);
    setFillPreviousCtc("");
    setCreateOpen(true);
    if (candidateId) setPendingCandidateId(candidateId);
  };

  const selectedSubject = dirMap[subjectId];

  const addBulkSubject = (id) => {
    if (!id) return;
    setSubjectIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
  };

  const removeBulkSubject = (id) => {
    setSubjectIds((ids) => ids.filter((x) => x !== id));
  };

  const switchRecipientKind = (kind) => {
    setRecipientKind(kind);
    setSubjectId("");
    setSubjectIds([]);
    setType("");
    setStep(1);
    setRequireAck(false);
  };

  const generatePreview = async () => {
    if (!subjectId || !type) return;
    setSaving(true);
    try {
      const payload =
        recipientKind === "candidate"
          ? { candidate_id: subjectId, type }
          : { employee_id: subjectId, type };
      const r = await api.post("/letters/preview", payload);
      setTitle(r.data.title);
      setBody(r.data.body);
      setIssueDate(r.data.issue_date);
      setPreviewLetterNo(r.data.letter_number || "");
      const missing = r.data?.checklist?.important_missing || [];
      const ctxPrev = (r.data?.context?.previous_ctc || "").trim();
      const bodyPrevEmpty = /Previous\s+Annual\s+CTC[\s\S]{0,80}:\s*<b>(?:\s|&nbsp;)*<\/b>/i.test(
        r.data.body || ""
      );
      setNeedPreviousCtc(
        type === "Compensation Revision Letter" &&
          (!ctxPrev || missing.includes("previous_ctc") || bodyPrevEmpty)
      );
      setFillPreviousCtc("");
      setStep(2);
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert(typeof d === "string" ? d : "Failed to load template");
    } finally {
      setSaving(false);
    }
  };

  const saveLetter = async () => {
    setSaving(true);
    try {
      let finalBody = body;
      if (needPreviousCtc && fillPreviousCtc.trim()) {
        finalBody = injectPreviousCtcIntoBody(body, fillPreviousCtc);
        setBody(finalBody);
      }
      const payload = {
        type,
        title,
        body: finalBody,
        issue_date: issueDate,
        require_acknowledgement: recipientKind === "candidate" ? false : requireAck,
      };
      if (recipientKind === "candidate") payload.candidate_id = subjectId;
      else payload.employee_id = subjectId;
      await api.post("/letters", payload);
      closeCreate();
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert(typeof d === "string" ? d : "Failed to save letter");
    } finally {
      setSaving(false);
    }
  };

  const saveBulk = async () => {
    if (!type || subjectIds.length === 0) return;
    setSaving(true);
    try {
      const payload = {
        type,
        issue_date: issueDate || undefined,
        require_acknowledgement: recipientKind === "candidate" ? false : requireAck,
        employee_ids: recipientKind === "employee" ? subjectIds : [],
        candidate_ids: recipientKind === "candidate" ? subjectIds : [],
      };
      const r = await api.post("/letters/bulk", payload);
      const n = r.data?.count ?? 0;
      const errs = r.data?.errors || [];
      if (errs.length) {
        alert(`Issued ${n} letter(s). ${errs.length} failed:\n${errs.slice(0, 5).map((e) => e.detail).join("\n")}`);
      }
      closeCreate();
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert(typeof d === "string" ? d : "Bulk issue failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this letter?")) return;
    await api.delete(`/letters/${id}`);
    load();
  };

  const downloadPdf = async (letter) => {
    const res = await api.get(`/exports/letter/${letter.id}.pdf`, { responseType: "blob" });
    const blob = new Blob([res.data]);
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `${letter.type.replace(/ /g, "_")}_${letter.employee_code || "letter"}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const ackBadge = (l) => {
    if (!l.require_acknowledgement && (l.acknowledgement_status || "NotRequired") === "NotRequired") {
      return <span className="text-xs text-slate-400">—</span>;
    }
    if (l.acknowledgement_status === "Acknowledged") {
      return (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Acknowledged</span>
      );
    }
    return (
      <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Pending ack</span>
    );
  };

  const subjectBadge = (l) => {
    if (l.candidate_id && !l.employee_id) {
      return <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">Candidate</span>;
    }
    if (l.candidate_id && l.employee_id) {
      return <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Hired</span>;
    }
    return null;
  };

  return (
    <AdminLayout title="HR Letters" breadcrumbs={["Home", "HR", "Letters"]}>
      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-[#226DB4]" /> Issued Letters
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={load}
              title="Refresh"
              data-testid="letters-refresh-btn"
              className="p-2 border border-slate-200 rounded hover:bg-slate-50 text-slate-600"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="letters-search-input"
                placeholder="Search name, code, type, letter no…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded w-72 focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4]"
              />
            </div>
            <select
              data-testid="letters-type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded bg-white"
            >
              <option value="">All types</option>
              {types.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <button
              onClick={() => startNew()}
              data-testid="letters-issue-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium"
            >
              <Plus className="h-4 w-4" /> Issue Letter
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <SortableTH columnKey="issue_date" active={sortBy} dir={sortDir} onClick={requestSort}>Date</SortableTH>
              <SortableTH columnKey="letter_number" active={sortBy} dir={sortDir} onClick={requestSort}>Letter No.</SortableTH>
              <SortableTH columnKey="employee_name" active={sortBy} dir={sortDir} onClick={requestSort}>Recipient</SortableTH>
              <SortableTH columnKey="type" active={sortBy} dir={sortDir} onClick={requestSort}>Type</SortableTH>
              <SortableTH columnKey="issued_by_name" active={sortBy} dir={sortDir} onClick={requestSort}>Issued By</SortableTH>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold">Status</th>
              <SortableTH columnKey="approved_by" active={sortBy} dir={sortDir} onClick={requestSort}>Approver</SortableTH>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold">Ack</th>
              <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.length === 0 ? (
              <EmptyState colSpan={9} message={search || typeFilter ? "No letters match your filters" : "No letters issued yet"} />
            ) : (
              sortedItems.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3 text-slate-700 font-medium">{formatDisplayDate(l.issue_date)}</td>
                  <td className="px-4 py-3 text-slate-700 font-mono text-xs">{l.letter_number || "—"}</td>
                  <td className="px-4 py-3 text-slate-800">
                    {l.employee_name} <span className="text-xs text-slate-500">({l.employee_code})</span>
                    {subjectBadge(l)}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{l.type}</td>
                  <td className="px-4 py-3 text-slate-600">{l.issued_by_name || "—"}</td>
                  <td className="px-4 py-3">{approvalBadge(l)}</td>
                  <td className="px-4 py-3 text-slate-600">{approvedByValue(l)}</td>
                  <td className="px-4 py-3">{ackBadge(l)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => setViewing(l)} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded" data-testid={`view-letter-${l.id}`}>
                        <Eye className="h-4 w-4" />
                      </button>
                      {canShowDownload(l) && (
                        <button onClick={() => downloadPdf(l)} className="p-1.5 text-[#226DB4] hover:bg-[#226DB4]/10 rounded" data-testid={`download-letter-${l.id}`}>
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => onDelete(l.id)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded" data-testid={`delete-letter-${l.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-[#f9fafb]">
              <h3 className="text-lg font-semibold text-slate-800">
                {mode === "bulk"
                  ? "Bulk Issue Letters"
                  : `Issue Letter — Step ${step} of 2`}
              </h3>
              <button onClick={closeCreate} className="p-2 text-slate-400 hover:bg-slate-100 rounded">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => { setMode("single"); setStep(1); }}
                  className={`px-3 py-1.5 text-sm rounded border ${
                    mode === "single" ? "bg-[#226DB4] text-white border-[#226DB4]" : "bg-white text-slate-700 border-slate-200"
                  }`}
                  data-testid="letter-mode-single"
                >
                  Single
                </button>
                <button
                  type="button"
                  onClick={() => { setMode("bulk"); setStep(1); }}
                  className={`px-3 py-1.5 text-sm rounded border ${
                    mode === "bulk" ? "bg-[#226DB4] text-white border-[#226DB4]" : "bg-white text-slate-700 border-slate-200"
                  }`}
                  data-testid="letter-mode-bulk"
                >
                  Bulk
                </button>
                <span className="mx-1 w-px bg-slate-200 self-stretch" />
                <button
                  type="button"
                  onClick={() => switchRecipientKind("employee")}
                  className={`px-3 py-1.5 text-sm rounded border ${
                    recipientKind === "employee" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-700 border-slate-200"
                  }`}
                  data-testid="letter-recipient-employee"
                >
                  Employee / Intern
                </button>
                <button
                  type="button"
                  onClick={() => switchRecipientKind("candidate")}
                  className={`px-3 py-1.5 text-sm rounded border ${
                    recipientKind === "candidate" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-700 border-slate-200"
                  }`}
                  data-testid="letter-recipient-candidate"
                >
                  Candidate
                </button>
              </div>

              {recipientKind === "candidate" && (
                <p className="text-xs text-slate-500 bg-sky-50 border border-sky-100 rounded px-3 py-2">
                  Pre-hire letters only: Offer Letter, Internship Offer Letter, or Contract Offer Letter. No EMP/INT code is burned.
                  Acknowledgement is not used (candidates have no login until hire).
                </p>
              )}

              {mode === "single" && step === 1 && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Letter Type</label>
                    <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls} data-testid="letter-type-select">
                      <option value="">Select type</option>
                      {issueTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">
                      {recipientKind === "candidate" ? "Candidate" : "Employee"}
                    </label>
                    <p className="text-xs text-slate-500 mb-1.5">
                      Search by name / code, or enter the exact code and click Find.
                    </p>
                    <EmployeeLookup
                      employees={directory}
                      value={subjectId}
                      onChange={setSubjectId}
                      testid="letter-employee-lookup"
                      placeholder={recipientKind === "candidate" ? "Search by name or candidate code…" : undefined}
                    />
                  </div>
                  {recipientKind === "employee" && (
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={requireAck}
                        onChange={(e) => setRequireAck(e.target.checked)}
                        data-testid="letter-require-ack"
                      />
                      Require employee acknowledgement
                    </label>
                  )}
                </>
              )}

              {mode === "single" && step === 2 && (
                <>
                  {selectedSubject && (
                    <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2">
                      Issuing <b>{type}</b> to <b>{empLabel(selectedSubject)}</b>
                      {recipientKind === "candidate" ? " (candidate)" : ""}
                      {previewLetterNo ? (
                        <span className="ml-2 font-mono text-xs text-slate-500">Ref: {previewLetterNo}</span>
                      ) : null}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Title</label>
                      <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Issue Date</label>
                      <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className={inputCls} />
                    </div>
                  </div>
                  {recipientKind === "employee" && (
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={requireAck}
                        onChange={(e) => setRequireAck(e.target.checked)}
                        data-testid="letter-require-ack-step2"
                      />
                      Require employee acknowledgement
                    </label>
                  )}
                  {needPreviousCtc && (
                    <div
                      className="rounded border border-amber-200 bg-amber-50 px-3 py-2 space-y-2"
                      data-testid="letter-fill-previous-ctc"
                    >
                      <div className="text-xs text-amber-900 font-medium">
                        Previous Annual CTC is empty (often because the employee master had no CTC when the revision was created). Enter it here — typing inside the empty bold space in the editor often does not save to PDF.
                      </div>
                      <div className="flex flex-wrap items-end gap-2">
                        <div className="flex-1 min-w-[160px]">
                          <label className="block text-[10px] font-semibold text-amber-800 uppercase mb-1">
                            Previous Annual CTC
                          </label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={fillPreviousCtc}
                            onChange={(e) => setFillPreviousCtc(e.target.value)}
                            placeholder="e.g. 450000"
                            className={inputCls}
                            data-testid="letter-previous-ctc-input"
                          />
                        </div>
                        <button
                          type="button"
                          className="px-3 py-2 text-sm bg-amber-700 hover:bg-amber-800 text-white rounded"
                          data-testid="letter-apply-previous-ctc"
                          onClick={() => {
                            if (!fillPreviousCtc.trim()) return;
                            setBody((b) => injectPreviousCtcIntoBody(b, fillPreviousCtc));
                            setNeedPreviousCtc(false);
                          }}
                        >
                          Apply to letter
                        </button>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Letter Body (editable)</label>
                    <LetterRichEditor
                      value={body}
                      onChange={setBody}
                      minHeight={360}
                      testid="letter-body-editor"
                    />
                  </div>
                </>
              )}

              {mode === "bulk" && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Letter Type</label>
                    <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls} data-testid="letter-bulk-type-select">
                      <option value="">Select type</option>
                      {issueTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Issue Date (optional)</label>
                    <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">
                      Add {recipientKind === "candidate" ? "candidates" : "employees"}
                    </label>
                    <p className="text-xs text-slate-500 mb-1.5">
                      Search and add each person. Template is rendered per person with a unique letter number.
                    </p>
                    <EmployeeLookup
                      employees={directory}
                      value=""
                      onChange={(id) => {
                        addBulkSubject(id);
                      }}
                      testid="letter-bulk-employee-lookup"
                      placeholder={recipientKind === "candidate" ? "Search by name or candidate code…" : undefined}
                    />
                    {subjectIds.length > 0 && (
                      <ul className="mt-3 flex flex-wrap gap-2" data-testid="letter-bulk-selected">
                        {subjectIds.map((id) => (
                          <li
                            key={id}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-slate-100 border border-slate-200 rounded text-slate-800"
                          >
                            {empLabel(dirMap[id]) || id}
                            <button type="button" onClick={() => removeBulkSubject(id)} className="text-slate-400 hover:text-slate-700">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="text-xs text-slate-500 mt-1.5">{subjectIds.length} selected (max 100)</div>
                  </div>
                  {recipientKind === "employee" && (
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={requireAck}
                        onChange={(e) => setRequireAck(e.target.checked)}
                        data-testid="letter-bulk-require-ack"
                      />
                      Require employee acknowledgement
                    </label>
                  )}
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-[#f9fafb] flex justify-between">
              <button
                onClick={() => {
                  if (mode === "single" && step === 2) setStep(1);
                  else closeCreate();
                }}
                className="px-4 py-2 text-sm border border-slate-200 rounded bg-white"
              >
                {mode === "single" && step === 2 ? "Back" : "Cancel"}
              </button>
              {mode === "single" && step === 1 && (
                <button
                  onClick={generatePreview}
                  disabled={!subjectId || !type || saving}
                  data-testid="letter-preview-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium disabled:opacity-70"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Generate Preview
                </button>
              )}
              {mode === "single" && step === 2 && (
                <button
                  onClick={saveLetter}
                  disabled={saving}
                  data-testid="letter-save-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-[#089949] hover:bg-[#067A3A] text-white rounded font-medium disabled:opacity-70"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save & Issue
                </button>
              )}
              {mode === "bulk" && (
                <button
                  onClick={saveBulk}
                  disabled={saving || !type || subjectIds.length === 0}
                  data-testid="letter-bulk-save-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-[#089949] hover:bg-[#067A3A] text-white rounded font-medium disabled:opacity-70"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Issue {subjectIds.length || ""} Letter{subjectIds.length === 1 ? "" : "s"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {viewing && (() => {
        const viewApprovedLine = approvedByViewLine(viewing);
        return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-[#f9fafb]">
              <h3 className="text-lg font-semibold text-slate-800">{viewing.title}</h3>
              <button onClick={() => setViewing(null)} className="p-2 text-slate-400 hover:bg-slate-100 rounded">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-8">
              <div className="text-sm text-slate-500 mb-4 space-y-1">
                <div>
                  Issued to <b>{viewing.employee_name}</b> on <b>{formatDisplayDate(viewing.issue_date)}</b>
                  {subjectBadge(viewing)}
                </div>
                {viewing.letter_number ? (
                  <div className="font-mono text-xs">Letter No: {viewing.letter_number}</div>
                ) : null}
                {viewing.issued_by_name ? (
                  <div className="text-xs text-slate-500">Issued by {viewing.issued_by_name}</div>
                ) : null}
                <div className="flex items-center gap-2">
                  {approvalBadge(viewing)}
                  {ackBadge(viewing)}
                </div>
                {viewApprovedLine ? (
                  <div className="text-xs text-slate-500">{viewApprovedLine}</div>
                ) : null}
              </div>
              <LetterBodyView body={viewing.body} letter={viewing} className="font-serif" />
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-[#f9fafb] flex justify-end gap-2">
              {canShowDownload(viewing) && (
                <button onClick={() => downloadPdf(viewing)} className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium">
                  <Download className="h-4 w-4" /> Download PDF
                </button>
              )}
              <button onClick={() => setViewing(null)} className="px-4 py-2 text-sm border border-slate-200 rounded">Close</button>
            </div>
          </div>
        </div>
        );
      })()}
    </AdminLayout>
  );
}
